import {
    useState,
    useRef,
    useEffect,
    useCallback,
    useId,
    type RefObject,
} from 'react';
import {
    Box,
    InputBase,
    IconButton,
    Tooltip,
    Typography,
    CircularProgress,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import { Link, useNavigate } from 'react-router-dom';
import { Badge, ProbBar } from './shared';
import { useHeaderSearch } from '../api/useUnifiedSearch';
import type { UnifiedAssetResult } from '../api/unifiedSearchTypes';
import type { EventFromAsset } from '../api/types';
import { AssetImage } from '../components/AssetImage';
import { PhoneSearchResults } from './PhoneSearchResults';
import { formatVolume } from './searchFormat';
import { phoneSurface } from '../../layout/chrome';
import { NO_MIN_TARGET } from '../../../root/touchTheme';
import {
    CloseIcon as OutlineCloseIcon,
    SearchIcon as OutlineSearchIcon,
} from '../../layout/mobile/icons';

const COLLAPSIBLE_OPEN_WIDTH = 320;

interface SearchBarProps {
    onClear?: () => void;
    fullWidth?: boolean;
    compact?: boolean;
    advancedHref?: string;
    /** Phone: 16 px input (no iOS zoom), 44 px row and buttons. */
    touch?: boolean;
    /**
     * Where the dropdown opens; 'above' for a field at the bottom of the
     * screen. Not used in touch mode: the phone panel is always above the field.
     */
    placement?: 'below' | 'above';
    /** Pixels, or a CSS length (e.g. a calc() that subtracts env() insets). */
    resultsMaxHeight?: number | string;
    onFocusChange?: (focused: boolean) => void;
    /**
     * Touch mode only. The element that holds the field and what belongs to
     * the search beside it (the dock's Cancel button): focus moving between
     * them is not leaving the search. Defaults to the bar itself.
     */
    focusScope?: RefObject<HTMLElement>;
    /** Only the icon until focused, then 320 px over the neighbours (condensed header). */
    collapsible?: boolean;
    /** Which edge a collapsible field keeps when it opens: 'right' opens leftwards (a field at the right end of the header). */
    anchor?: 'left' | 'right';
    /** The outline icons of the phone mockups instead of the Material ones. Always on in touch mode. */
    outlineIcons?: boolean;
}

export function SearchBar({
    onClear,
    fullWidth,
    compact,
    advancedHref,
    touch,
    placement = 'below',
    resultsMaxHeight = 400,
    onFocusChange,
    focusScope,
    collapsible,
    anchor = 'left',
    outlineIcons,
}: SearchBarProps) {
    const theme = useTheme();
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const [focused, setFocused] = useState(false);
    const [selectedIdx, setSelectedIdx] = useState(-1);
    const inputRef = useRef<HTMLInputElement>(null);
    const rootRef = useRef<HTMLDivElement>(null);
    const blurTimer = useRef<ReturnType<typeof setTimeout>>();
    const inputId = useId();
    // Open only while focused. A query left in the field after the user
    // tapped away stays in it (the text is not lost) but the field folds back
    // to its icon instead of hovering over the tabs.
    const expanded = !collapsible || focused;
    const iconOnly = collapsible && !expanded;

    // One place that reports focus, whichever path changed it (focus, blur
    // timer, choosing a result, Escape, clear). The callback is read through a
    // ref so that a caller passing a new function on every render cannot
    // re-fire it: it runs when `focused` changes, never because of identity.
    const onFocusChangeRef = useRef(onFocusChange);
    useEffect(() => {
        onFocusChangeRef.current = onFocusChange;
    });
    const focusedRef = useRef(false);
    useEffect(() => {
        focusedRef.current = focused;
        onFocusChangeRef.current?.(focused);
    }, [focused]);
    // An unmounting input fires no blur, so a bar that goes away while it has
    // focus (the dock drops its search row on /markets/search) has to say so,
    // or the caller stays in "searching" for good.
    useEffect(
        () => () => {
            clearTimeout(blurTimer.current);
            if (focusedRef.current) onFocusChangeRef.current?.(false);
        },
        []
    );

    const { assets, activeEvents: events, loading } = useHeaderSearch(query);

    const totalCount = assets.length + events.length;
    const hasResults = focused && (totalCount > 0 || loading);
    // On a phone the panel is also the way to Advanced search while the field
    // has focus (the button beside the field gives way to Cancel), so it stays
    // up for that link even when nothing matched.
    const showPhonePanel =
        touch && focused && (totalCount > 0 || loading || !!advancedHref);
    const outline = touch || outlineIcons;

    const handleSelectAsset = useCallback(
        (a: UnifiedAssetResult) => {
            setFocused(false);
            setQuery('');
            inputRef.current?.blur();
            navigate(`/markets/asset/${a.asset_type}/${a.ticker}`);
        },
        [navigate]
    );

    const handleSelectEvent = useCallback(
        (e: EventFromAsset) => {
            setFocused(false);
            setQuery('');
            inputRef.current?.blur();
            navigate(`/markets/event/${e.slug || e.event_id}`);
        },
        [navigate]
    );

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            setFocused(false);
            inputRef.current?.blur();
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIdx(prev => (prev < totalCount - 1 ? prev + 1 : 0));
            return;
        }
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIdx(prev => (prev > 0 ? prev - 1 : totalCount - 1));
            return;
        }
        if (e.key === 'Enter' && selectedIdx >= 0) {
            e.preventDefault();
            if (selectedIdx < assets.length) {
                handleSelectAsset(assets[selectedIdx]);
            } else {
                const eventIdx = selectedIdx - assets.length;
                if (events[eventIdx]) handleSelectEvent(events[eventIdx]);
            }
        }
    };

    useEffect(() => {
        setSelectedIdx(-1);
    }, [query]);

    // A tap on the phone panel's empty space ends the search: iOS does not
    // blur a field when the tap lands on content that is not interactive.
    const handleDismiss = useCallback(() => {
        setFocused(false);
        inputRef.current?.blur();
    }, []);

    // Phone, with a hardware keyboard: the field, its Clear button, the
    // results panel (down to the Advanced search row) and whatever the caller
    // names beside the field (the dock's Cancel) are ONE focus scope. Tab and
    // Shift+Tab inside it are not leaving the search; it ends 200 ms after
    // focus has left all of it. (The timer, not an immediate end: a focus move
    // between two of them can be seen as a blur first and a focus after, and
    // some browsers give no relatedTarget.) Native listeners on the scope
    // because Cancel is outside this component's own DOM.
    useEffect(() => {
        if (!touch) return;
        const scope = focusScope?.current ?? rootRef.current;
        if (!scope) return;
        const onFocusIn = () => clearTimeout(blurTimer.current);
        const onFocusOut = (e: FocusEvent) => {
            clearTimeout(blurTimer.current);
            const next = e.relatedTarget as Node | null;
            if (next && scope.contains(next)) return;
            blurTimer.current = setTimeout(() => setFocused(false), 200);
        };
        // Escape ends the search from anywhere in the scope, not only from
        // the field (focus may be on Clear or on Cancel). Without a search
        // there is nothing to end, and focus stays where it is (the dock's
        // Advanced search icon is in the scope too).
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'Escape' || !focusedRef.current) return;
            clearTimeout(blurTimer.current);
            setFocused(false);
            (document.activeElement as HTMLElement | null)?.blur();
        };
        scope.addEventListener('focusin', onFocusIn);
        scope.addEventListener('focusout', onFocusOut);
        scope.addEventListener('keydown', onKeyDown);
        return () => {
            scope.removeEventListener('focusin', onFocusIn);
            scope.removeEventListener('focusout', onFocusOut);
            scope.removeEventListener('keydown', onKeyDown);
        };
    }, [touch, focusScope]);

    const handleClear = () => {
        setQuery('');
        onClear?.();
        if (touch) {
            // Phone: the X empties the field, it does not end the search.
            // Focus back in case the press took it (and keep the keyboard).
            inputRef.current?.focus();
        } else {
            setFocused(false);
        }
    };

    const isDark = theme.palette.mode === 'dark';
    const borderColor = focused
        ? theme.palette.primary.main
        : theme.palette.divider;
    const showClear = expanded && !!query;
    // Placeholder and icons: 54% of the text colour (MUI's action.active is
    // plain white in the dark theme, brighter than what is typed).
    const mutedText = alpha(theme.palette.text.primary, 0.54);
    // The phone pill of the mockup: 44 px, white, fully rounded, 1 px border
    // that is 2 px and primary while it has focus. The padding gives the extra
    // border pixel back so the icon and the text do not move.
    const borderWidth = focused ? 2 : 1;
    const touchPill = touch
        ? {
              boxSizing: 'border-box',
              height: 44,
              minHeight: 44,
              py: 0,
              gap: 1,
              pl: `${15 - borderWidth}px`,
              pr: `${(showClear ? 5 : 15) - borderWidth}px`,
              bgcolor: isDark ? alpha('#fff', 0.08) : phoneSurface(theme),
              borderWidth,
              borderStyle: 'solid',
              borderColor: focused
                  ? theme.palette.primary.main
                  : alpha(theme.palette.text.primary, 0.15),
              borderRadius: '22px',
              cursor: 'text',
          }
        : {};
    const SearchGlyph = outline ? OutlineSearchIcon : SearchIcon;
    const iconOnlyOutline = iconOnly && outlineIcons;

    return (
        <Box
            ref={rootRef}
            sx={{
                // On a phone the results are as wide as the screen: they
                // resolve against the dock, not against this box.
                position: touch ? 'static' : 'relative',
                maxWidth: fullWidth ? '100%' : compact ? '100%' : 560,
                width: collapsible ? 44 : '100%',
            }}
        >
            {/* Input container (on a phone the whole pill is the label) */}
            <Box
                {...(touch ? { component: 'label', htmlFor: inputId } : {})}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: compact ? 0.75 : 1.25,
                    px: compact ? 1.25 : 1.75,
                    py: compact ? '2px' : '7px',
                    bgcolor: isDark
                        ? alpha('#fff', compact ? 0.08 : 0.04)
                        : alpha('#000', compact ? 0.06 : 0.03),
                    border: 1,
                    borderColor: compact ? 'transparent' : borderColor,
                    borderRadius: hasResults
                        ? placement === 'above'
                            ? '0 0 6px 6px'
                            : '6px 6px 0 0'
                        : '6px',
                    transition: 'border-color 0.15s',
                    minHeight: touch ? 44 : undefined,
                    ...touchPill,
                    ...(collapsible && expanded
                        ? {
                              // Opens over the tabs instead of pushing them:
                              // the outer box stays 44 px wide and the field
                              // overflows it to the right. It stays in the
                              // flow (not absolute) so the outer box keeps its
                              // height and the results open right below it.
                              position: 'relative',
                              width: COLLAPSIBLE_OPEN_WIDTH,
                              // Anchored right: the field's right edge stays
                              // where the icon is, so it opens leftwards.
                              ...(anchor === 'right'
                                  ? {
                                        marginLeft: `${44 - COLLAPSIBLE_OPEN_WIDTH}px`,
                                    }
                                  : {}),
                              zIndex: 2,
                              bgcolor: 'background.default',
                              boxShadow: 3,
                          }
                        : {}),
                    ...(iconOnly
                        ? {
                              bgcolor: 'transparent',
                              // No gap: the (empty, zero wide) input beside the
                              // icon would otherwise push it 6 px off centre.
                              gap: 0,
                              px: 0,
                              // the 44 px icon + border fit the 48 px toolbar
                              py: 0,
                              justifyContent: 'center',
                          }
                        : {}),
                }}
            >
                <Box
                    // On a phone the pill itself is the label (a label inside
                    // a label is invalid).
                    {...(touch
                        ? { component: 'span' }
                        : { component: 'label', htmlFor: inputId })}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'text',
                        // A tap target of 44 x 44 while it is the only thing there
                        minWidth: iconOnly ? 44 : collapsible ? 36 : undefined,
                        minHeight: iconOnly ? 44 : collapsible ? 36 : undefined,
                    }}
                >
                    <SearchGlyph
                        sx={{
                            fontSize: touch
                                ? 20
                                : iconOnlyOutline
                                  ? 24
                                  : compact
                                    ? 16
                                    : 18,
                            color: touch
                                ? mutedText
                                : iconOnlyOutline
                                  ? alpha(theme.palette.text.primary, 0.7)
                                  : 'text.disabled',
                        }}
                    />
                </Box>
                <InputBase
                    id={inputId}
                    inputRef={inputRef}
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    onFocus={() => {
                        // A refocus inside the 200 ms must not be undone by
                        // the timer of the blur before it.
                        clearTimeout(blurTimer.current);
                        setFocused(true);
                    }}
                    onBlur={() => {
                        // Phone: the focus scope (see above) decides.
                        if (touch) return;
                        clearTimeout(blurTimer.current);
                        blurTimer.current = setTimeout(
                            () => setFocused(false),
                            200
                        );
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder="Search ticker or market..."
                    inputProps={{
                        'aria-label': 'Search',
                        style: touch ? { fontSize: 16 } : undefined,
                    }}
                    sx={{
                        flex: 1,
                        fontSize: compact ? '12px' : '13px',
                        color: compact ? 'inherit' : undefined,
                        '& input::placeholder': {
                            color: touch
                                ? mutedText
                                : compact
                                  ? 'inherit'
                                  : 'text.disabled',
                            opacity: touch ? 1 : compact ? 0.6 : 1,
                        },
                        // The condensed header's field is 12 px for a mouse;
                        // a touch screen (an iPad in landscape) zooms into
                        // anything under 16 px when it takes focus.
                        ...(collapsible
                            ? {
                                  '@media (pointer: coarse)': {
                                      '& input': { fontSize: 16 },
                                  },
                              }
                            : {}),
                        ...(iconOnly ? { flex: 0, width: 0 } : {}),
                    }}
                />
                {loading && !(touch && !query.trim()) && (
                    <CircularProgress
                        size={compact ? 12 : 14}
                        sx={{ color: 'text.disabled' }}
                    />
                )}
                {showClear && (
                    <IconButton
                        size="small"
                        aria-label="Clear search"
                        onMouseDown={
                            touch ? e => e.preventDefault() : undefined
                        }
                        onClick={handleClear}
                        sx={{
                            p: 0.25,
                            // 36 px to look at, 44 px to hit: the extra 4 px
                            // all round belong to the button. NO_MIN_TARGET:
                            // on a touch screen the theme would make the
                            // visible button 44 px as well.
                            ...(touch
                                ? {
                                      ...NO_MIN_TARGET,
                                      width: 36,
                                      height: 36,
                                      p: 0,
                                      flexShrink: 0,
                                      '&::after': {
                                          content: '""',
                                          position: 'absolute',
                                          inset: -4,
                                      },
                                  }
                                : {}),
                        }}
                    >
                        {touch ? (
                            <OutlineCloseIcon
                                sx={{
                                    fontSize: 18,
                                    color: mutedText,
                                }}
                            />
                        ) : (
                            <CloseIcon
                                sx={{
                                    fontSize: compact ? 14 : 16,
                                    color: 'text.disabled',
                                }}
                            />
                        )}
                    </IconButton>
                )}
                {/* Not on a phone: the dock has the button, outside the pill. */}
                {expanded && advancedHref && !touch && (
                    <Tooltip title="Advanced search" disableInteractive>
                        <IconButton
                            component={Link}
                            to={advancedHref}
                            size="small"
                            onMouseDown={e => e.preventDefault()}
                            sx={{
                                p: 0.25,
                                color: 'inherit',
                                opacity: 0.7,
                            }}
                        >
                            <ManageSearchIcon
                                sx={{ fontSize: compact ? 15 : 17 }}
                            />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            {/* Phone: a full-bleed panel, see PhoneSearchResults */}
            {showPhonePanel && (
                <PhoneSearchResults
                    assets={assets}
                    events={events}
                    query={query}
                    selectedIdx={selectedIdx}
                    advancedHref={advancedHref}
                    maxHeight={resultsMaxHeight}
                    onSelectAsset={handleSelectAsset}
                    onSelectEvent={handleSelectEvent}
                    onDismiss={handleDismiss}
                />
            )}

            {/* Dropdown */}
            {!touch && hasResults && (
                <Box
                    data-testid="search-results"
                    data-placement={placement}
                    sx={{
                        position: 'absolute',
                        // A collapsible bar's box is only 44 px wide; the
                        // results are as wide as the open field instead
                        // (and end at its right edge when it opens leftwards).
                        ...(collapsible
                            ? {
                                  width: COLLAPSIBLE_OPEN_WIDTH,
                                  ...(anchor === 'right'
                                      ? { right: 0 }
                                      : { left: 0 }),
                              }
                            : { left: 0, right: 0 }),
                        zIndex: 1300,
                        bgcolor: 'background.paper',
                        border: 1,
                        borderColor: 'divider',
                        ...(placement === 'above'
                            ? {
                                  bottom: '100%',
                                  borderBottom: 'none',
                                  borderRadius: '6px 6px 0 0',
                              }
                            : {
                                  top: '100%',
                                  borderTop: 'none',
                                  borderRadius: '0 0 6px 6px',
                              }),
                        boxShadow: isDark
                            ? '0 8px 24px rgba(0,0,0,0.3)'
                            : '0 8px 24px rgba(0,0,0,0.08)',
                        maxHeight: resultsMaxHeight,
                        overflowY: 'auto',
                    }}
                >
                    {/* Assets section */}
                    {assets.map((a, i) => {
                        const globalIdx = i;
                        return (
                            <Box
                                key={`asset-${a.ticker}`}
                                onMouseDown={e => {
                                    e.preventDefault();
                                    handleSelectAsset(a);
                                }}
                                sx={{
                                    px: 2,
                                    py: 1.25,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    bgcolor:
                                        selectedIdx === globalIdx
                                            ? 'action.hover'
                                            : 'transparent',
                                    '&:hover': {
                                        bgcolor: 'action.hover',
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.25,
                                        flex: 1,
                                        minWidth: 0,
                                    }}
                                >
                                    {a.image && (
                                        <AssetImage
                                            assetType={a.asset_type}
                                            ticker={a.ticker}
                                            size={24}
                                        />
                                    )}
                                    <Typography
                                        sx={{
                                            fontFamily: 'monospace',
                                            fontSize: '13px',
                                            fontWeight: 700,
                                        }}
                                    >
                                        {a.ticker}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: '13px',
                                            color: 'text.secondary',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {a.asset_name}
                                    </Typography>
                                </Box>
                                <Badge color={theme.palette.primary.main}>
                                    {a.asset_type}
                                </Badge>
                            </Box>
                        );
                    })}

                    {/* Events section */}
                    {events.length > 0 && (
                        <Box>
                            {assets.length > 0 && (
                                <Box
                                    sx={{
                                        borderTop: 1,
                                        borderColor: 'divider',
                                    }}
                                />
                            )}
                            <Typography
                                sx={{
                                    px: 2,
                                    pt: 1,
                                    pb: 0.5,
                                    fontSize: '10.5px',
                                    color: 'text.disabled',
                                    fontWeight: 600,
                                    letterSpacing: '0.08em',
                                    textTransform: 'uppercase',
                                }}
                            >
                                EVENTS
                            </Typography>
                            {events.map((ev, i) => {
                                const globalIdx = assets.length + i;
                                return (
                                    <Box
                                        key={`event-${ev.event_id}`}
                                        onMouseDown={e => {
                                            e.preventDefault();
                                            handleSelectEvent(ev);
                                        }}
                                        sx={{
                                            px: 2,
                                            py: 1.25,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.25,
                                            bgcolor:
                                                selectedIdx === globalIdx
                                                    ? 'action.hover'
                                                    : 'transparent',
                                            '&:hover': {
                                                bgcolor: 'action.hover',
                                            },
                                        }}
                                    >
                                        {ev.image && (
                                            <Box
                                                component="img"
                                                src={ev.image}
                                                sx={{
                                                    width: 28,
                                                    height: 28,
                                                    borderRadius: '4px',
                                                    objectFit: 'cover',
                                                    flexShrink: 0,
                                                }}
                                            />
                                        )}
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography
                                                sx={{
                                                    fontSize: '13px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {ev.event_title}
                                            </Typography>
                                            {ev.market_count != null && (
                                                <Typography
                                                    sx={{
                                                        fontSize: '11px',
                                                        color: 'text.disabled',
                                                    }}
                                                >
                                                    {ev.market_count} market
                                                    {ev.market_count !== 1
                                                        ? 's'
                                                        : ''}
                                                    {ev.volume != null &&
                                                        ev.volume > 0 &&
                                                        ` · ${formatVolume(ev.volume)}`}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
}
